<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsWebsiteAudit } from "../data/getFaqsWebsiteAudit.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getReasonsAudit } from "../data/getReasonsAudit.js";
	import { getWebsiteAuditProcess } from "../data/getWebsiteAuditProcess.js";
	import { getWebsiteAuditWhen } from "../data/getWebsiteAuditWhen.js";
	import type { WebsiteAuditProps } from "./WebsiteAuditProps.js";

	const { partners }: WebsiteAuditProps = $props();
	const { tenant } = getGlobalTemplateProps();
</script>

<Layout withStructuredData>
	<Section
		paddingFollowNext
		background={Background.LightGradient}
		centered
		gridHeadline="container-6"
		gridDesc="container-6"
		hero
	>
		{#snippet headline()}
			<Formatted text="Odhalíme cg[[slabá místa]] webu a dáme ho cg[[do pořádku]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Jsou vaše stránky pomalé, neplní požadované cíle nebo se na nich nakupily jiné problémy, které po sobě zanechal původní dodavatel? sp[[Pomocí auditu potíže odhalíme i opravíme.]]"
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci nezávaznou konzultaci" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Najdeme řešení i na zdánlivě neřešitelné cg[[problémy s webem]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Není nic horšího, než investovat stovky tisíc Kč do firemního webu a pak zjistit, že je plný sp[[technických problémů]], sp[[nepřináší dostatek zakázek]] nebo sp[[pokulhává jeho obsahová stránka]]."
				/>
			</p>
			<p>
				<Formatted
					text="Ve Fastest jsme připraveni odhalit slabá místa stránek a vyřešit i zdánlivě neřešitelné zádrhely. Ať už máte obsáhlý web vyvinutý na míru, e-shop nebo jen základní firemní prezentaci na platformách typu WordPress či Wix, v rámci auditu a optimalizace stránek vám hodíme záchranné lano."
				/>
			</p>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
		>
			<Formatted text="Nezávazně poptat" />
			<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
		</Link>
	</Section>

	<Section
		background={Background.LightGradient}
		noPadding="bot"
		gridHeadline="container-6 lg:text-center"
		gridDesc="container-8"
		centered
	>
		{#snippet headline()}
			<Formatted text="Kdy využít cg[[audit a optimalizaci webu]]?" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getWebsiteAuditWhen(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} />
			{/each}
		</div>
	</Section>

	<Section
		noPadding="bot"
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Vezmeme si pod lupu cg[[technické i marketingové nedostatky]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Na weby nahlížíme komplexně]], tedy nejen z technického, ale i z uživatelského, marketingového a obchodního hlediska. S čím vám v rámci auditu a optimalizace pomůžeme?"
				/>
			</p>
			<div class="flex flex-col md:flex-row md:gap-16">
				<ul class="custom-list">
					<li><Formatted text="Technické problémy webu" /></li>
					<li><Formatted text="Optimalizace stránek pro mobily" /></li>
					<li><Formatted text="User Experience (uživatelská přívětivost)" /></li>
					<li><Formatted text="Obsahová stránka webu" /></li>
					<li><Formatted text="Indexace ve vyhledávačích a technické SEO" /></li>
					<li><Formatted text="Soulad webu s legislativou (např. cookies)" /></li>
					<li><Formatted text="Zabezpečení webu" /></li>
					<li><Formatted text="Optimalizace konverzí na webu" /></li>
					<li><Formatted text="Webová analytika a reporting" /></li>
					<li><Formatted text="Nastavení marketingových nástrojů" /></li>
				</ul>
			</div>
		{/snippet}
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Zeptat se na podrobnosti" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section background={Background.LightGradient} centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Komu už jsme cg[[pomohli]]?" />
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Crm)} />

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="Jak při cg[[auditu]] a optimalizaci stránek cg[[postupujeme]]" />
			<span class="block text-2md font-bold mt-6">
				<Formatted
					text="Provedeme vás celým procesem od úvodní konzultace přes audit až po řešení zjištěných nedostatků."
				/>
			</span>
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getWebsiteAuditProcess(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci vaše doporučení
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Časté otázky k cg[[auditu a optimalizaci]] webu" />
		{/snippet}
		<FaqItems faqs={getFaqsWebsiteAudit(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[4 důvody]], proč vsadit na audit webu od Fastest" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol background={Background.Onyx} items={getReasonsAudit(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci audit webu
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
